/**
 * /devhub/ (index) — DevHub home (ControlPlane operator cockpit). Surface Layer (/01).
 *
 * REQ-001.91 DevHub UI rebuild · Phase 1. The landing rendered inside the `/devhub` layout: LIVE status
 * tiles + a clickable OperatorDataGrid (the Phase-0 kit) wired to live `useControlActionFrames` — every
 * row opens the OperatorDetailTearsheet. Operator-only (the layout + routePolicy guard; self-guarded here).
 */

import { ClickableTile, Column, Grid, InlineNotification, SkeletonText, Tile } from '@carbon/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import type { OperatorColumn, OperatorDetailMode } from '@contracts/devhub-kit.contract';
import { useEntitlements } from '@lib/persistence/useEntitlements';
import {
  useControlActionFrames,
  useNotifications,
  useStatus,
  type ControlFrameRow,
} from '@lib/persistence/useRuntimeData';
import { AiAffordanceLabel, OperatorDataGrid, OperatorDetailTearsheet } from '../components/devhub-kit';

export const Route = createFileRoute('/devhub/')({ component: DevHubHome });

const FRAME_COLUMNS: OperatorColumn<ControlFrameRow>[] = [
  { key: 'id', header: 'Frame' },
  { key: 'intent', header: 'Intent' },
  { key: 'status', header: 'Status', isStatus: true },
  { key: 'actor', header: 'Actor' },
  { key: 'risk_level', header: 'Risk' },
  { key: 'created_at', header: 'Created' },
];

function DevHubHome() {
  const { isElevated } = useEntitlements();
  const { status, isLoading: statusLoading } = useStatus();
  const { openActionable } = useNotifications(undefined, 100);
  const { frames, isLoading: framesLoading } = useControlActionFrames(20);
  const navigate = useNavigate();
  const [selected, setSelected] = useState<ControlFrameRow | null>(null);
  const [mode, setMode] = useState<OperatorDetailMode>('view');

  const apiVersion =
    status?.version == null
      ? '—'
      : typeof status.version === 'string'
        ? status.version
        : status.version.api;

  if (!isElevated) {
    return (
      <main className="view">
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Operator access required"
          subtitle="The DevHub control plane is operator-only. Sign in with an operator role."
        />
      </main>
    );
  }

  return (
    <main className="view">
      <header className="view__header">
        <h1 className="view__title">DevHub</h1>
        <AiAffordanceLabel />
        <p className="view__subtitle">Operator cockpit — live runtime, queue and approvals.</p>
      </header>

      <Grid className="view__section" narrow>
        <Column sm={4} md={4} lg={5}>
          <Tile className="devhub-tile">
            <p className="devhub-tile__label">BFF API</p>
            {statusLoading && !status ? (
              <SkeletonText />
            ) : (
              <p className="devhub-tile__value">v{apiVersion}</p>
            )}
          </Tile>
        </Column>
        <Column sm={4} md={4} lg={5}>
          <ClickableTile
            className="devhub-tile"
            onClick={() => navigate({ to: '/devhub/governance' })}
            aria-label="Open the governance / approvals queue"
          >
            <p className="devhub-tile__label">Approvals awaiting</p>
            <p className="devhub-tile__value">{openActionable}</p>
            <p className="devhub-tile__hint">Open Governance →</p>
          </ClickableTile>
        </Column>
        <Column sm={4} md={4} lg={5}>
          <Tile className="devhub-tile">
            <p className="devhub-tile__label">Recent frames</p>
            <p className="devhub-tile__value">{frames.length}</p>
          </Tile>
        </Column>
      </Grid>

      <section className="view__section">
        <h2 className="devhub-section-title">Recent ActionFrames</h2>
        <OperatorDataGrid
          ariaLabel="Recent ActionFrames"
          columns={FRAME_COLUMNS}
          rows={frames}
          getRowId={(row) => row.id}
          onRowClick={(row) => {
            setSelected(row);
            setMode('view');
          }}
          loading={framesLoading}
          emptyLabel="No ActionFrames in the queue yet."
        />
      </section>

      <OperatorDetailTearsheet
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.id ?? ''}
        subtitle={selected?.intent ?? ''}
        mode={mode}
        onModeChange={setMode}
        fields={
          selected
            ? [
                { label: 'Intent', value: selected.intent },
                { label: 'Tool route', value: selected.tool_route },
                { label: 'Status', value: selected.status },
                { label: 'Actor', value: selected.actor },
                { label: 'Risk level', value: selected.risk_level },
                { label: 'Requires approval', value: selected.requires_human_approval ? 'yes' : 'no' },
                { label: 'Created', value: selected.created_at },
              ]
            : []
        }
      />
    </main>
  );
}
